import { notify, confirmBottom, checkedFetch } from './feedback';
import React, { useState, useEffect } from 'react';

export default function BOM({ API_BASE }) {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  const [newProductName, setNewProductName] = useState('');
  const [newProductPrice, setNewProductPrice] = useState('');
  const [selectedProduct, setSelectedProduct] = useState(null);
  
  const [components, setComponents] = useState([]);
  const [newCompName, setNewCompName] = useState('');
  const [newCompCost, setNewCompCost] = useState('');
  const [newCompQuantity, setNewCompQuantity] = useState(1);
  const [newCompCurrency, setNewCompCurrency] = useState('NGN');

  const [usdRate, setUsdRate] = useState(() => localStorage.getItem('bom_usd_rate') || '1500');
  const [cnyRate, setCnyRate] = useState(() => localStorage.getItem('bom_cny_rate') || '200');

  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState('');
  const [editCost, setEditCost] = useState('');
  const [editQuantity, setEditQuantity] = useState(1);
  const [editCurrency, setEditCurrency] = useState('NGN');

  const [searchTerm, setSearchTerm] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

  useEffect(() => {
    localStorage.setItem('bom_usd_rate', usdRate);
  }, [usdRate]);

  useEffect(() => {
    localStorage.setItem('bom_cny_rate', cnyRate);
  }, [cnyRate]);

  useEffect(() => {
    fetchProducts();
  }, [API_BASE]);

  const fetchProducts = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/v1/bom-products`);
      if (!res.ok) throw new Error('Failed to fetch products');
      const data = await res.json();
      setProducts(data);
    } catch (err) {
      setError(err.message);
    }
    setLoading(false);
  };

  const handleCreateProduct = async (e) => {
    e.preventDefault();
    if (!newProductName.trim()) return;
    try {
      const res = await checkedFetch(`${API_BASE}/v1/bom-products`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: newProductName, price: parseFloat(newProductPrice) || 0 }),
      });
      if (res.ok) {
        notify('Product created.', 'success');
        setNewProductName('');
        setNewProductPrice('');
        fetchProducts();
      }
    } catch (err) {
      notify(err.message);
    }
  };

  const handleDeleteProduct = async (id) => {
    if (!(await confirmBottom('Delete this product and all its components?'))) return;
    try {
      await checkedFetch(`${API_BASE}/v1/bom-products/${id}`, { method: 'DELETE' });
      if (selectedProduct?.id === id) setSelectedProduct(null);
      notify('Product deleted.', 'success');
      fetchProducts();
    } catch (err) {
      notify(err.message);
    }
  };

  const handleUpdatePrice = async (id, newPrice) => {
    try {
      const res = await checkedFetch(`${API_BASE}/v1/bom-products/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ price: parseFloat(newPrice) || 0 }),
      });
      if (res.ok) {
        notify('Product price saved.', 'success');
        setSelectedProduct(prev => ({ ...prev, price: parseFloat(newPrice) || 0 }));
        fetchProducts();
      }
    } catch (err) {
      notify(err.message);
    }
  };

  const loadProduct = async (prod) => {
    setSelectedProduct(prod);
    setComponents([]);
    setEditingId(null);
    try {
      const res = await fetch(`${API_BASE}/v1/bom-products/${prod.id}/components`);
      if (res.ok) {
        const data = await res.json();
        setComponents(data);
      }
    } catch (err) {
      notify(err.message);
    }
  };

  const handleAddComponent = async (e) => {
    e.preventDefault();
    if (!newCompName.trim() || !newCompCost) return;
    try {
      const res = await checkedFetch(`${API_BASE}/v1/bom-components`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          product_id: selectedProduct.id,
          name: newCompName,
          cost: parseFloat(newCompCost),
          quantity: parseInt(newCompQuantity, 10) || 1,
          currency: newCompCurrency
        }),
      });
      if (res.ok) {
        notify('Component created.', 'success');
        setNewCompName('');
        setNewCompCost('');
        setNewCompQuantity(1);
        setNewCompCurrency('NGN');
        loadProduct(selectedProduct);
      }
    } catch (err) {
      notify(err.message);
    }
  };

  const handleSaveEdit = async (id) => {
    try {
      const res = await checkedFetch(`${API_BASE}/v1/bom-components/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: editName,
          cost: parseFloat(editCost),
          quantity: parseInt(editQuantity, 10) || 1,
          currency: editCurrency
        }),
      });
      if (res.ok) {
        notify('Component changes saved.', 'success');
        setEditingId(null);
        loadProduct(selectedProduct);
      } else {
        const data = await res.json();
        notify(data.error || 'Failed to update component');
      }
    } catch (err) {
      notify(err.message);
    }
  };

  const handleDeleteComponent = async (id) => {
    if (!(await confirmBottom("Are you sure you want to remove this component?"))) return;
    try {
      await checkedFetch(`${API_BASE}/v1/bom-components/${id}`, { method: 'DELETE' });
      notify('Component deleted.', 'success');
      loadProduct(selectedProduct);
    } catch (err) {
      notify(err.message);
    }
  };

  const calculateNgnCost = (cost, currency, quantity = 1) => {
    const numCost = Number(cost) * parseInt(quantity, 10);
    if (currency === 'USD') return numCost * Number(usdRate);
    if (currency === 'CNY') return numCost * Number(cnyRate);
    return numCost; // Default NGN
  };

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const filteredAndSortedComponents = React.useMemo(() => {
    let filtered = components;
    if (searchTerm) {
      filtered = filtered.filter(c => c.name.toLowerCase().includes(searchTerm.toLowerCase()));
    }
    if (sortConfig.key) {
      filtered = [...filtered].sort((a, b) => {
        let aVal = a[sortConfig.key];
        let bVal = b[sortConfig.key];

        if (sortConfig.key === 'unit_cost_ngn') {
          aVal = calculateNgnCost(a.cost, a.currency || 'NGN', 1);
          bVal = calculateNgnCost(b.cost, b.currency || 'NGN', 1);
        } else if (sortConfig.key === 'total_cost_ngn') {
          aVal = calculateNgnCost(a.cost, a.currency || 'NGN', a.quantity || 1);
          bVal = calculateNgnCost(b.cost, b.currency || 'NGN', b.quantity || 1);
        } else if (sortConfig.key === 'name') {
          aVal = (aVal || '').toLowerCase();
          bVal = (bVal || '').toLowerCase();
        }

        if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
        if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
      });
    }
    return filtered;
  }, [components, searchTerm, sortConfig, usdRate, cnyRate]);

  if (loading) return <p>Loading BOM...</p>;
  if (error) return <p className="error-message">{error}</p>;

  const totalCostNgn = components.reduce((sum, c) => sum + calculateNgnCost(c.cost, c.currency || 'NGN', c.quantity || 1), 0);
  const totalCostUsd = Number(usdRate) > 0 ? totalCostNgn / Number(usdRate) : 0;
  
  const productPrice = selectedProduct?.price || 0;
  const productPriceUsd = Number(usdRate) > 0 ? productPrice / Number(usdRate) : 0;
  const profitNgn = productPrice - totalCostNgn;
  const profitUsd = Number(usdRate) > 0 ? profitNgn / Number(usdRate) : 0;
  const profitMargin = productPrice > 0 ? ((profitNgn / productPrice) * 100).toFixed(1) : 0;

  return (
    <div className="bom-page">
      <div className="responsive-row" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '20px' }}>
        <div>
          <h1>Bill of Materials (BOM)</h1>
          <p style={{ color: '#666', fontSize: '13px', marginBottom: '20px' }}>
            Create products and add components to calculate total production cost.
          </p>
        </div>
        
        <div className="responsive-row" style={{ display: 'flex', gap: '15px', background: '#f8f9fa', padding: '10px 15px', borderRadius: '8px', border: '1px solid #ddd' }}>
          <div>
            <label style={{ display: 'block', fontSize: '11px', color: '#666', marginBottom: '2px', fontWeight: 'bold' }}>USD Rate (₦)</label>
            <input 
              aria-label="USD exchange rate in naira"
              type="number" 
              value={usdRate} 
              onChange={e => setUsdRate(e.target.value)} 
              style={{ width: '80px', padding: '6px', border: '1px solid #ccc', borderRadius: '4px', backgroundColor: '#fff', color: '#000' }}
            />
          </div>
          <div>
            <label style={{ display: 'block', fontSize: '11px', color: '#666', marginBottom: '2px', fontWeight: 'bold' }}>Yuan Rate (₦)</label>
            <input 
              aria-label="Yuan exchange rate in naira"
              type="number" 
              value={cnyRate} 
              onChange={e => setCnyRate(e.target.value)} 
              style={{ width: '80px', padding: '6px', border: '1px solid #ccc', borderRadius: '4px', backgroundColor: '#fff', color: '#000' }}
            />
          </div>
        </div>
      </div>

      {!selectedProduct ? (
        <div className="bom-dashboard">
          <form onSubmit={handleCreateProduct} style={{ marginBottom: '20px', display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
            <input
              type="text"
              placeholder="New Product Name (e.g., LED Controller)"
              value={newProductName}
              onChange={(e) => setNewProductName(e.target.value)}
              style={{ padding: '10px', flex: '1 1 200px', borderRadius: '4px', border: '1px solid #ccc', backgroundColor: '#fff', color: '#000' }}
            />
            <input
              type="number"
              placeholder="Selling Price (₦)"
              value={newProductPrice}
              onChange={(e) => setNewProductPrice(e.target.value)}
              style={{ padding: '10px', flex: '0 1 150px', borderRadius: '4px', border: '1px solid #ccc', backgroundColor: '#fff', color: '#000' }}
            />
            <button type="submit" style={{ padding: '10px 20px', background: '#0070f3', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer', fontWeight: 'bold', flex: '1 1 auto', whiteSpace: 'nowrap' }}>
              Create Product
            </button>
          </form>

          <div className="cards-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(min(250px, 100%), 1fr))', gap: '20px' }}>
            {products.length === 0 ? <p>No products found. Create one above!</p> : null}
            {products.map(prod => (
              <div 
                key={prod.id} 
                className="card" 
                onClick={() => loadProduct(prod)}
                style={{ position: 'relative', padding: '20px', background: '#fff', border: '1px solid #ddd', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.05)', cursor: 'pointer' }}
              >
                <h2 style={{ color: '#0070f3', margin: '0 0 10px 0', fontSize: '18px' }}>{prod.name}</h2>
                <p style={{ fontSize: '12px', color: '#888', margin: 0 }}>Created: {new Date(prod.created_at).toLocaleDateString()}</p>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div className="bom-details">
          <div className="responsive-row" style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '20px', flexWrap: 'wrap', gap: '10px' }}>
            <button onClick={() => setSelectedProduct(null)} style={{ background: '#eee', border: 'none', padding: '8px 16px', borderRadius: '4px', cursor: 'pointer', color: '#000' }}>
              ← Back to Products
            </button>
            <button onClick={() => handleDeleteProduct(selectedProduct.id)} style={{ background: '#dc3545', border: 'none', color: '#fff', padding: '8px 16px', borderRadius: '4px', cursor: 'pointer', fontWeight: 'bold' }}>
              Delete Product
            </button>
          </div>

          <div className="bom-stats-header">
            <h2 style={{ margin: 0, marginBottom: '10px' }}>{selectedProduct.name}</h2>
            
            <div className="bom-selling-price" style={{ marginBottom: '15px', display: 'flex', alignItems: 'center', gap: '10px' }}>
              <label style={{ fontWeight: 'bold', fontSize: '14px' }}>Selling Price (₦):</label>
              <input 
                type="number"
                value={selectedProduct.price || ''}
                onChange={(e) => setSelectedProduct(prev => ({...prev, price: e.target.value}))}
                onBlur={(e) => handleUpdatePrice(selectedProduct.id, e.target.value)}
                style={{ padding: '6px', borderRadius: '4px', border: '1px solid #ccc', width: '150px', backgroundColor: '#fff', color: '#000' }}
                placeholder="Enter price..."
              />
              <span style={{ fontSize: '13px', color: '#666' }}>
                (${productPriceUsd.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})})
              </span>
            </div>

            <div className="bom-stats-container">
              <div className="bom-stat-item">
                <div className="bom-stat-label">Total Cost (USD)</div>
                <div className="bom-stat-value usd">${totalCostUsd.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</div>
              </div>
              <div className="bom-stat-item">
                <div className="bom-stat-label">Total Cost (NGN)</div>
                <div className="bom-stat-value ngn">₦{totalCostNgn.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</div>
              </div>
              <div className="bom-stat-item" style={{ background: profitNgn >= 0 ? '#e8f5e9' : '#ffebee' }}>
                <div className="bom-stat-label">Profit (NGN / USD)</div>
                <div className="bom-stat-value" style={{ color: profitNgn >= 0 ? '#2e7d32' : '#c62828' }}>
                  ₦{profitNgn.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})} / 
                  ${profitUsd.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                  <div style={{ fontSize: '12px', marginTop: '4px' }}>Margin: {profitMargin}%</div>
                </div>
              </div>
            </div>
          </div>

          <form onSubmit={handleAddComponent} className="bom-add-form">
            <input
              type="text"
              placeholder="Component/Part Name"
              value={newCompName}
              onChange={(e) => setNewCompName(e.target.value)}
              required
              className="bom-input name" aria-label="Component or part name"
            />
            <input
              type="number"
              placeholder="Qty"
              value={newCompQuantity}
              onChange={(e) => setNewCompQuantity(e.target.value)}
              required
              min="1"
              className="bom-input qty" aria-label="Quantity"
            />
            <input
              type="number"
              placeholder="Unit Cost"
              value={newCompCost}
              onChange={(e) => setNewCompCost(e.target.value)}
              required
              step="any"
              className="bom-input cost" aria-label="Unit cost"
            />
            <select
              value={newCompCurrency}
              onChange={(e) => setNewCompCurrency(e.target.value)}
              className="bom-input currency" aria-label="Currency"
            >
              <option value="NGN">₦ NGN</option>
              <option value="USD">$ USD</option>
              <option value="CNY">¥ CNY</option>
            </select>
            <button type="submit" className="bom-btn-add">
              + Add
            </button>
          </form>

          <div className="responsive-row" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '20px', marginBottom: '10px', flexWrap: 'wrap', gap: '10px' }}>
            <h3 style={{ margin: 0 }}>Components</h3>
            <input
              type="text"
              placeholder="Search components..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{ padding: '8px', borderRadius: '4px', border: '1px solid #ccc', width: '100%', maxWidth: '250px', backgroundColor: '#fff', color: '#000' }}
            />
          </div>
          <div className="table-container" tabIndex={0} role="region" aria-label="BOM components; scroll horizontally for more columns" style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch', width: '100%' }}>
            <table className="expenses-table" style={{ minWidth: '700px', width: '100%' }}>
            <thead>
              <tr style={{ background: '#f8f9fa', borderBottom: '2px solid #ddd' }}>
                <th style={{ padding: '12px', textAlign: 'left', cursor: 'pointer' }} onClick={() => handleSort('name')}>
                  Component/Part {sortConfig.key === 'name' ? (sortConfig.direction === 'asc' ? '↑' : '↓') : ''}
                </th>
                <th style={{ width: '80px', textAlign: 'center', padding: '12px', cursor: 'pointer' }} onClick={() => handleSort('quantity')}>
                  Qty {sortConfig.key === 'quantity' ? (sortConfig.direction === 'asc' ? '↑' : '↓') : ''}
                </th>
                <th style={{ width: '150px', textAlign: 'right', padding: '12px', cursor: 'pointer' }} onClick={() => handleSort('unit_cost_ngn')}>
                  Unit Cost {sortConfig.key === 'unit_cost_ngn' ? (sortConfig.direction === 'asc' ? '↑' : '↓') : ''}
                </th>
                <th style={{ width: '150px', textAlign: 'right', padding: '12px', cursor: 'pointer' }} onClick={() => handleSort('total_cost_ngn')}>
                  Total (₦) {sortConfig.key === 'total_cost_ngn' ? (sortConfig.direction === 'asc' ? '↑' : '↓') : ''}
                </th>
                <th style={{ width: '120px', textAlign: 'center', padding: '12px' }}>Action</th>
              </tr>
            </thead>
            <tbody>
              {filteredAndSortedComponents.length === 0 ? (
                <tr><td colSpan="5" style={{ textAlign: 'center', padding: '30px', color: '#666' }}>No components found.</td></tr>
              ) : (
                filteredAndSortedComponents.map(comp => (
                  <React.Fragment key={comp.id}>
                    {editingId === comp.id ? (
                      <tr style={{ borderBottom: '1px solid #eee', backgroundColor: '#f0f8ff' }}>
                        <td style={{ padding: '12px' }}>
                          <input type="text" value={editName} onChange={e => setEditName(e.target.value)} style={{ width: '100%', padding: '8px', border: '1px solid #ccc', borderRadius: '4px', backgroundColor: '#fff', color: '#000' }} />
                        </td>
                        <td style={{ padding: '12px', textAlign: 'center' }}>
                          <input type="number" min="1" value={editQuantity} onChange={e => setEditQuantity(e.target.value)} style={{ width: '60px', padding: '8px', border: '1px solid #ccc', borderRadius: '4px', backgroundColor: '#fff', color: '#000', textAlign: 'center' }} />
                        </td>
                        <td colSpan="2" style={{ padding: '12px', textAlign: 'right' }}>
                          <div className="responsive-row" style={{ display: 'flex', gap: '5px', justifyContent: 'flex-end' }}>
                            <input type="number" step="any" value={editCost} onChange={e => setEditCost(e.target.value)} style={{ width: '80px', padding: '8px', border: '1px solid #ccc', borderRadius: '4px', backgroundColor: '#fff', color: '#000' }} />
                            <select value={editCurrency} onChange={e => setEditCurrency(e.target.value)} style={{ padding: '8px', border: '1px solid #ccc', borderRadius: '4px', backgroundColor: '#fff', color: '#000' }}>
                              <option value="NGN">NGN</option>
                              <option value="USD">USD</option>
                              <option value="CNY">CNY</option>
                            </select>
                          </div>
                        </td>
                        <td style={{ textAlign: 'center', padding: '12px' }}>
                          <button onClick={() => handleSaveEdit(comp.id)} style={{ background: '#28a745', color: '#fff', border: 'none', cursor: 'pointer', padding: '6px 12px', borderRadius: '4px', marginRight: '5px', marginBottom: '5px', width: '100%' }}>Save</button>
                          <button onClick={() => setEditingId(null)} style={{ background: '#6c757d', color: '#fff', border: 'none', cursor: 'pointer', padding: '6px 12px', borderRadius: '4px', width: '100%' }}>Cancel</button>
                        </td>
                      </tr>
                    ) : (
                      <tr style={{ borderBottom: '1px solid #eee' }}>
                        <td style={{ padding: '12px' }}>{comp.name}</td>
                        <td style={{ padding: '12px', textAlign: 'center' }}>{comp.quantity || 1}</td>
                        <td style={{ textAlign: 'right', padding: '12px' }}>
                          {(comp.currency === 'USD' || comp.currency === 'CNY') && (
                            <div style={{ fontSize: '12px', color: '#888', marginBottom: '2px' }}>
                              {comp.currency === 'USD' ? '$' : '¥'}{Number(comp.cost).toLocaleString()}
                            </div>
                          )}
                          <strong style={{ fontSize: '14px' }}>₦{calculateNgnCost(comp.cost, comp.currency || 'NGN', 1).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</strong>
                        </td>
                        <td style={{ textAlign: 'right', padding: '12px' }}>
                          <strong style={{ fontSize: '16px', color: '#16a34a' }}>₦{calculateNgnCost(comp.cost, comp.currency || 'NGN', comp.quantity || 1).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</strong>
                        </td>
                        <td style={{ textAlign: 'center', padding: '12px' }}>
                          <button onClick={() => {
                            setEditingId(comp.id);
                            setEditName(comp.name);
                            setEditCost(comp.cost);
                            setEditCurrency(comp.currency || 'NGN');
                            setEditQuantity(comp.quantity || 1);
                          }} style={{ background: 'transparent', color: '#0070f3', border: 'none', cursor: 'pointer', fontSize: '14px', padding: '4px 8px', borderRadius: '4px', marginRight: '5px' }}>Edit</button>
                          <button onClick={() => handleDeleteComponent(comp.id)} style={{ background: 'transparent', color: '#dc3545', border: 'none', cursor: 'pointer', fontSize: '16px', padding: '4px 8px', borderRadius: '4px' }}>✕</button>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))
              )}
            </tbody>
          </table>
          </div>
        </div>
      )}
    </div>
  );
}
